import { reportOnce } from '@protector/api-client';
import type { MethodologyPayload } from '@protector/entities/methodology/models';
import type { CommandRequest, UUID } from '@protector/utils/types';
import axios, { type AxiosError } from 'axios';
import { UpdateMethodologyDeep } from 'entities/methodology-deep/methodology-deep.actions';
import { captureError } from 'infra/monitoring/monitoring.utils';
import { ofType, type ActionsObservable } from 'redux-observable';
import { of } from 'rxjs';
import { catchError, concatMap, map } from 'rxjs/operators';
import {
  LoadMethodologiesByCompanyFailure,
  LoadMethodologiesByCompanySuccess,
  MethodologyActionTypes,
  UpdateMethodologyFailure,
  UpdateMethodologySuccess,
  type LoadMethodologiesByCompany,
  type UpdateMethodology
} from './methodology.actions';
import { getMethodologyByCompanyShallow, updateMethodology } from './methodology.service';

export const handleLoadMethodologyByCompany = (action$: ActionsObservable<ReturnType<typeof LoadMethodologiesByCompany>>) =>
  action$.pipe(
    ofType(MethodologyActionTypes.LOAD_METHODOLOGIES_BY_COMPANY),
    map(({ payload }) => payload),
    concatMap((companyId: UUID) =>
      getMethodologyByCompanyShallow(companyId).pipe(
        map(response => {
          if (!response.content) {
            reportOnce(
              'methodology.epics:handleLoadMethodologyByCompany:missingContent',
              () => new Error('methodology.epics: getMethodologyByCompanyShallow response.content missing -- falling back to []'),
              { errorType: 'StateFallback', domain: 'Methodologies' }
            );
          }
          const availableMethodologyArray = (response.content ?? []).filter(m => !m.deleted_at);

          return LoadMethodologiesByCompanySuccess(availableMethodologyArray);
        })
      )
    ),
    catchError((err: string) => of(LoadMethodologiesByCompanyFailure(err)))
  );

export const handleUpdateMethodology = (action$: ActionsObservable<ReturnType<typeof UpdateMethodology>>) =>
  action$.pipe(
    ofType(MethodologyActionTypes.UPDATE_METHODOLOGY),
    map(({ payload }) => payload),
    concatMap((methodologyPayload: CommandRequest<MethodologyPayload>) =>
      updateMethodology(methodologyPayload).pipe(
        concatMap(() => {
          return [
            UpdateMethodologySuccess(methodologyPayload.payload.methodology_dto),
            UpdateMethodologyDeep(methodologyPayload.payload.methodology_deep)
          ];
        })
      )
    ),
    catchError((err: Error | AxiosError) => {
      let errorObject: Error = err;
      if (axios.isAxiosError(err)) {
        errorObject = new Error(err.response?.statusText);
      }
      captureError(errorObject, { errorType: 'AxiosError', domain: 'Methodologies' });

      return of(UpdateMethodologyFailure(err.message));
    })
  );
